import { useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { PaperPlaneTilt, ArrowCounterClockwise, Globe, CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";

export const fmtMoney = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/* "Send to client portal" with a confirm step. `url` = POST endpoint; `body` optional. */
export function PortalPushButton({ url, body, amount, disabledReason, onDone, testId = "send-to-portal-btn", size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const push = async () => {
    setBusy(true);
    try {
      const { data } = await API.post(url, body || {});
      toast.success(`Live in the client portal — ${data.notified || 0} portal user${data.notified === 1 ? "" : "s"} notified`);
      setOpen(false);
      onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send to the portal"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <span title={disabledReason || ""}>
        <Button size={size} className={`bg-purple-600 hover:bg-purple-700 text-white ${className}`} disabled={!!disabledReason} onClick={() => setOpen(true)} data-testid={testId}>
          <PaperPlaneTilt size={13} className="mr-1" /> Send to client portal
        </Button>
      </span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="send-to-portal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe size={18} className="text-purple-600" /> Send to client portal?</DialogTitle>
            <DialogDescription>
              The quote{amount != null ? ` (${fmtMoney(amount)} ex VAT)` : ""} goes live in the client's portal straight away. Their portal users get an in-app alert and an email with a link to approve or decline. You can withdraw it later to make changes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="send-to-portal-cancel">Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={push} disabled={busy} data-testid="send-to-portal-confirm">{busy ? "Sending…" : "Send now"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Shown while the quote sits in the portal awaiting a decision. */
export function PortalLiveBadge({ pushedAt, notified, withdrawUrl, onDone, testId = "portal-live-badge" }) {
  const [busy, setBusy] = useState(false);
  const withdraw = async () => {
    if (!window.confirm("Withdraw this quote from the client portal? The approval card disappears for the client until you send it again.")) return;
    setBusy(true);
    try { await API.post(withdrawUrl, {}); toast.success("Withdrawn from the client portal"); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't withdraw"); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-purple-300 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30 px-2.5 py-1.5 text-xs" data-testid={testId}>
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-purple-500 opacity-60 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-purple-600" /></span>
      <span className="font-semibold text-purple-800 dark:text-purple-200">Live in client portal</span>
      <span className="text-purple-700/80 dark:text-purple-300/80">since {fmtWhen(pushedAt)}{notified != null ? ` · ${notified} notified` : ""}</span>
      {withdrawUrl && (
        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-purple-800 dark:text-purple-200" onClick={withdraw} disabled={busy} data-testid={`${testId}-withdraw`}>
          <ArrowCounterClockwise size={12} className="mr-1" /> Withdraw
        </Button>
      )}
    </div>
  );
}

/* The client's answer, pushed back from the portal. */
export function ClientDecisionBanner({ response, amount, onConvert, convertLabel = "Convert to job", testId = "client-decision-banner" }) {
  if (!response || response.via !== "client_portal") return null;
  const approved = response.decision ? response.decision === "approve" : true;
  const who = `${response.name || "Client"}${response.client ? ` (${response.client})` : ""}`;
  return (
    <div className={`rounded-lg border-2 p-4 flex flex-col md:flex-row md:items-center gap-3 ${approved ? "border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/30" : "border-red-400 bg-red-50 dark:border-red-700 dark:bg-red-950/30"}`} data-testid={testId} data-decision={approved ? "approve" : "decline"}>
      {approved ? <CheckCircle size={26} weight="fill" className="text-emerald-600 shrink-0" /> : <XCircle size={26} weight="fill" className="text-red-600 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{approved ? "Approved" : "Declined"} by {who} via the client portal · {fmtWhen(response.at)}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {amount != null && <span>{fmtMoney(amount)} ex VAT</span>}
          {response.po_number && <span className="ml-2 inline-flex items-center rounded bg-background border border-border px-1.5 py-0.5 font-mono text-[11px]" data-testid={`${testId}-po`}>PO: {response.po_number}</span>}
          {response.comment && <span className="block italic mt-1">“{response.comment}”</span>}
        </p>
      </div>
      {approved && onConvert && (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" onClick={onConvert} data-testid={`${testId}-convert`}>
          {convertLabel} <ArrowRight size={12} className="ml-1" />
        </Button>
      )}
    </div>
  );
}
